import { useNavigate } from "@solidjs/router";
import { createSignal, onCleanup, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  REDIRECT_SELECTION_SNAPSHOT_KEY,
  type SelectedFreelancer,
} from "@/domains/company/active-search/store/freelance-search-store";
import type { FreelancerSearchFilters } from "@/domains/company/active-search/types";
import { useGenerateJobOfferFromProfiles } from "@/domains/job-offer/ai/api";
import { useAiChatStore } from "@/domains/job-offer/ai/store/AiChatProvider";
import { mapDraftToPayload } from "./map-draft";

const MAX_INSTRUCTIONS_LENGTH = 2000;

type AiFromProfilesGenerationModalProps = {
  open: boolean;
  onClose: () => void;
  freelancerIds: string[];
  selectedFreelancers: SelectedFreelancer[];
  filters: FreelancerSearchFilters;
};

export function AiFromProfilesGenerationModal(
  props: AiFromProfilesGenerationModalProps,
) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const aiChat = useAiChatStore();
  const generateMutation = useGenerateJobOfferFromProfiles();

  const [instructions, setInstructions] = createSignal("");
  const [errorVisible, setErrorVisible] = createSignal(false);

  // Leaving the page (Back, a link) while generating does not cancel the
  // request: once it answers, nothing may be written or navigated anymore.
  let disposed = false;
  onCleanup(() => {
    disposed = true;
  });

  const handleGenerate = async () => {
    setErrorVisible(false);

    try {
      const response = await generateMutation.mutateAsync({
        freelancer_ids: props.freelancerIds,
        filters: props.filters,
        instructions: instructions().trim() || undefined,
      });
      if (disposed) return;

      const mapped = mapDraftToPayload(response.data);
      aiChat.reset();
      aiChat.initChat();
      aiChat.updatePartialPayload(mapped);
      sessionStorage.setItem(
        REDIRECT_SELECTION_SNAPSHOT_KEY,
        JSON.stringify(props.selectedFreelancers),
      );

      navigate(
        "/job-offers/create/refine?next=%2Fprofile%2Fcompany%2Fsearch%2Ffreelancers%2Fsend-proposal&from=ai_generation",
      );
    } catch {
      if (disposed) return;
      setErrorVisible(true);
    }
  };

  return (
    <Dialog
      open={props.open}
      onOpenChange={(open) => {
        // Esc and overlay are ignored while generating: closing now would
        // still let the running request navigate to the refine page.
        if (!open && !generateMutation.isPending) props.onClose();
      }}
    >
      <DialogContent
        class="bg-white"
        // The X would do nothing while generating (see `onOpenChange`).
        hideCloseButton={generateMutation.isPending}
      >
        <DialogHeader>
          <DialogTitle>
            {t(
              "company.search.freelancers.new_proposal.method_selector.modal.title",
            )}
          </DialogTitle>
          <DialogDescription>
            {t(
              "company.search.freelancers.new_proposal.method_selector.modal.description",
            )}
          </DialogDescription>
        </DialogHeader>

        <div class="space-y-2">
          <label
            for="ai-generation-instructions"
            class="text-sm font-medium text-foreground"
          >
            {t(
              "company.search.freelancers.new_proposal.method_selector.modal.instructions_label",
            )}
          </label>
          <textarea
            id="ai-generation-instructions"
            class="flex min-h-30 w-full rounded-md border border-input bg-white px-3 py-2 text-sm shadow-xs transition-shadow placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-[1.5px] focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 resize-none"
            placeholder={String(
              t(
                "company.search.freelancers.new_proposal.method_selector.modal.instructions_placeholder",
              ),
            )}
            value={instructions()}
            onInput={(e) => {
              const value = e.currentTarget.value;
              if (value.length <= MAX_INSTRUCTIONS_LENGTH) {
                setInstructions(value);
              }
            }}
            maxLength={MAX_INSTRUCTIONS_LENGTH}
            disabled={generateMutation.isPending}
          />
          <p class="text-xs text-muted-foreground text-right">
            {t(
              "company.search.freelancers.new_proposal.method_selector.modal.char_count",
              {
                count: String(instructions().length),
                max: String(MAX_INSTRUCTIONS_LENGTH),
              },
            )}
          </p>
        </div>

        <Show when={errorVisible()}>
          <p class="text-sm text-destructive">
            {t(
              "company.search.freelancers.new_proposal.method_selector.modal.error_text",
            )}
          </p>
        </Show>

        <DialogFooter class="pt-2">
          <Button
            onClick={handleGenerate}
            disabled={generateMutation.isPending}
          >
            <Show when={generateMutation.isPending}>
              <i class="ph ph-spinner animate-spin mr-2" />
            </Show>
            {generateMutation.isPending
              ? t(
                  "company.search.freelancers.new_proposal.method_selector.modal.generating_button",
                )
              : t(
                  "company.search.freelancers.new_proposal.method_selector.modal.generate_button",
                )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
